import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useDeepCompareEffect } from 'ahooks';
import { noop } from 'lodash-es';

import { appConfig } from '@ll-platform/frontend/config/app.config';
import { analyticsDebugLog } from '@ll-platform/frontend/core/analytics/activityTracker';

export enum AnalyticsCustomIdentifyContextEnum {
  Project = 'project', // key=project.id
  ProjectGoogleDocCallSheetExperiment = 'projectGoogleDocCallSheetExperiment', // key=true|false
  ProjectStyle = 'projectStyle', // key=project.style
}

type CustomIdentifyValue = {
  key: string;
} & Record<string, unknown>;

export type AnalyticsCustomIdentifyContextType = {
  contexts: Map<AnalyticsCustomIdentifyContextEnum, CustomIdentifyValue>;
  setContext: (
    name: AnalyticsCustomIdentifyContextEnum,
    value: CustomIdentifyValue | null,
  ) => void;
};

const defaultContext: AnalyticsCustomIdentifyContextType = {
  contexts: new Map(),
  setContext: noop,
};

const AnalyticsCustomIdentifyContext =
  createContext<AnalyticsCustomIdentifyContextType>(defaultContext);
AnalyticsCustomIdentifyContext.displayName = 'AnalyticsCustomIdentifyContext';

export const AnalyticsCustomIdentifyContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [contexts, setContexts] = useState<
    AnalyticsCustomIdentifyContextType['contexts']
  >(defaultContext.contexts);

  const setContext = useCallback(
    (
      name: AnalyticsCustomIdentifyContextEnum,
      value: CustomIdentifyValue | null,
    ) => {
      setContexts((current) => {
        if (appConfig.REACT_APP_ANALYTICS_DEBUG) {
          analyticsDebugLog('Set Custom Identify Context', { name, value });
        }
        const newContexts = new Map(current);
        if (value) {
          newContexts.set(name, value);
        } else {
          newContexts.delete(name);
        }

        return newContexts;
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      contexts,
      setContext,
    }),
    [contexts, setContext],
  );

  return (
    <AnalyticsCustomIdentifyContext.Provider value={value}>
      {children}
    </AnalyticsCustomIdentifyContext.Provider>
  );
};

export const useAnalyticsCustomIdentifyContext = () => {
  return useContext(AnalyticsCustomIdentifyContext);
};

// To prevent race conditions, please only register this component once per context name
type GlobalSetAnalyticsCustomIdentifyContextProps = PropsWithChildren & {
  name: AnalyticsCustomIdentifyContextEnum;
  value: CustomIdentifyValue | null;
};

export function GlobalSetAnalyticsCustomIdentifyContext({
  children,
  name,
  value,
}: GlobalSetAnalyticsCustomIdentifyContextProps) {
  const { setContext } = useAnalyticsCustomIdentifyContext();
  useDeepCompareEffect(() => {
    setContext(name, value);
  }, [name, value, setContext]);

  useEffect(() => {
    return () => {
      setContext(name, null);
    };
  }, [name, setContext]);

  return children;
}
